import { useCallback, useMemo, useState } from 'react'
import { Collapse } from '@/shared/components/Collapse'
import {
  useGetMachineryDirectiveCompliance,
  useListStandardApplicability,
  buildStandardsHazardPreviewItems,
  type StandardApplicability,
  type StandardsHazardPreviewItem,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useListUserStandards } from '@/shared/hooks/api/userStandards'
import type { UserStandard } from '@/shared/hooks/api/userStandards'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { StandardDataStatusBadge } from '@/components/StandardDataStatusBadge'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item'
import { Skeleton } from '@/components/ui/skeleton'
function applicabilityNeedsDataBadge(sa: StandardApplicability): boolean {
  const hasData =
    sa.hasSignificantHazards === true ||
    sa.hasInformationForUse === true ||
    sa.hasRiskReductionMeasures === true
  return !hasData
}

function groupByStandard(
  items: StandardsHazardPreviewItem[]
): Map<string, StandardsHazardPreviewItem[]> {
  const map = new Map<string, StandardsHazardPreviewItem[]>()
  for (const item of items) {
    const ref = item.standardReference
    const list = map.get(ref) ?? []
    list.push(item)
    map.set(ref, list)
  }
  return map
}

export type GenerateFromStandardsModalProps = {
  isOpen: boolean
  onClose: () => void
  complianceId: string
  onGenerate: (selectedHazardIds: string[]) => void
  isGenerating?: boolean
  /** Non-empty significant hazard ids already generated (AGENT) for this compliance */
  generatedSignificantHazardIds?: ReadonlySet<string>
}

export function GenerateFromStandardsModal({
  isOpen,
  onClose,
  complianceId,
  onGenerate,
  isGenerating = false,
  generatedSignificantHazardIds = new Set(),
}: GenerateFromStandardsModalProps) {
  const { data: compliance } = useGetMachineryDirectiveCompliance(
    isOpen ? complianceId : undefined
  )
  const displayLocale = compliance?.contentLanguage
  const {
    data: applicability,
    isLoading: isLoadingApplicability,
    error: applicabilityError,
  } = useListStandardApplicability(complianceId)

  const acceptedApplicabilityWithUserStandard = useMemo(
    () =>
      applicability?.filter(
        (applicableStandard) =>
          applicableStandard.isAccepted === true &&
          applicableStandard.userStandardId != null
      ) ?? [],
    [applicability]
  )

  const { data: userStandardsList = [], isLoading: isLoadingUserStandards } =
    useListUserStandards({
      enabled: isOpen && acceptedApplicabilityWithUserStandard.length > 0,
    })

  const userStandardsById = useMemo(() => {
    const map = new Map(
      userStandardsList
        .filter((userStandard) =>
          acceptedApplicabilityWithUserStandard.some(
            (applicableStandard) =>
              applicableStandard.userStandardId === userStandard.id
          )
        )
        .map((userStandard) => [userStandard.id, userStandard])
    )
    return map
  }, [userStandardsList, acceptedApplicabilityWithUserStandard])

  const standardsForHazards = useMemo((): UserStandard[] => {
    return acceptedApplicabilityWithUserStandard
      .map((applicableStandard) => {
        const userStandard =
          applicableStandard.userStandardId != null
            ? userStandardsById.get(applicableStandard.userStandardId)
            : undefined
        return userStandard ?? null
      })
      .filter((standard): standard is UserStandard => standard != null)
  }, [acceptedApplicabilityWithUserStandard, userStandardsById])

  const items = useMemo(
    () =>
      buildStandardsHazardPreviewItems(
        standardsForHazards,
        acceptedApplicabilityWithUserStandard.map(
          (applicableStandard) => applicableStandard.reference
        ),
        displayLocale
      ),
    [
      standardsForHazards,
      acceptedApplicabilityWithUserStandard,
      displayLocale,
    ]
  )
  const grouped = useMemo(() => groupByStandard(items), [items])

  const isLoading =
    isLoadingApplicability ||
    (acceptedApplicabilityWithUserStandard.length > 0 && isLoadingUserStandards)
  const error = applicabilityError

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className='flex max-h-[85vh] flex-col gap-4 sm:max-w-2xl'
        showCloseButton={!isGenerating}
      >
        {isOpen ? (
          <GenerateFromStandardsModalBody
            grouped={grouped}
            items={items}
            acceptedApplicability={acceptedApplicabilityWithUserStandard}
            isLoading={isLoading}
            error={error}
            isGenerating={isGenerating}
            onGenerate={onGenerate}
            onClose={onClose}
            generatedSignificantHazardIds={generatedSignificantHazardIds}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

type ModalBodyProps = {
  grouped: Map<string, StandardsHazardPreviewItem[]>
  items: StandardsHazardPreviewItem[]
  acceptedApplicability: StandardApplicability[]
  isLoading: boolean
  error: Error | null
  isGenerating: boolean
  onGenerate: (selectedHazardIds: string[]) => void
  onClose: () => void
  generatedSignificantHazardIds: ReadonlySet<string>
}

function selectableIdsForGroup(groupItems: StandardsHazardPreviewItem[]) {
  return groupItems.map((item) => item.id).filter((id) => id !== '')
}

function GenerateFromStandardsModalBody({
  grouped,
  items,
  acceptedApplicability,
  isLoading,
  error,
  isGenerating,
  onGenerate,
  onClose,
  generatedSignificantHazardIds,
}: ModalBodyProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [collapsedStandards, setCollapsedStandards] = useState<Set<string>>(
    new Set()
  )

  const toggleCollapsed = useCallback((standardRef: string) => {
    setCollapsedStandards((prev) => {
      const next = new Set(prev)
      if (next.has(standardRef)) {
        next.delete(standardRef)
      } else {
        next.add(standardRef)
      }
      return next
    })
  }, [])

  const toggleId = useCallback((id: string) => {
    if (id === '') {
      return
    }
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const toggleStandard = useCallback(
    (standardRef: string) => {
      const groupItems = grouped.get(standardRef) ?? []
      const ids = selectableIdsForGroup(groupItems)
      if (ids.length === 0) {
        return
      }
      const allSelected = ids.every((id) => selectedIds.has(id))
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (allSelected) {
          ids.forEach((id) => next.delete(id))
        } else {
          ids.forEach((id) => next.add(id))
        }
        return next
      })
    },
    [grouped, selectedIds]
  )

  const selectedList = useMemo(() => Array.from(selectedIds), [selectedIds])
  const canGenerate = selectedList.length > 0 && !isGenerating

  const handleGenerate = useCallback(() => {
    if (!canGenerate) {
      return
    }
    onGenerate(selectedList)
    onClose()
  }, [canGenerate, selectedList, onGenerate, onClose])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const listRows = useMemo(() => {
    const rows: Array<{
      key: string
      sa: StandardApplicability
      groupItems: StandardsHazardPreviewItem[]
    }> = []
    for (const sa of acceptedApplicability) {
      const groupItems = grouped.get(sa.reference) ?? []
      if (groupItems.length > 0) {
        rows.push({ key: sa.id, sa, groupItems })
        continue
      }
      rows.push({ key: sa.id, sa, groupItems: [] })
    }
    return rows
  }, [acceptedApplicability, grouped])

  const hasAnyHazards = items.length > 0

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {t('hazardsPage.generateFromStandardsModal.title')}
        </DialogTitle>
      </DialogHeader>

      <div className='min-h-0 flex-1 space-y-4 overflow-y-auto'>
        {isLoading && (
          <div className='flex justify-center py-8'>
            <Skeleton className='size-10 rounded-full' />
          </div>
        )}
        {error != null && (
          <p className='text-sm text-destructive'>
            {t('hazardsPage.generateFromStandardsModal.loadFailed', {
              message: error.message,
            })}
          </p>
        )}
        {!isLoading && error == null && listRows.length === 0 && (
          <p className='py-6 text-center text-muted-foreground'>
            {acceptedApplicability.length === 0
              ? t('hazardsPage.generateFromStandardsModal.noHazardsFound')
              : t(
                  'hazardsPage.generateFromStandardsModal.noHazardsButHasStandards'
                )}
          </p>
        )}
        {!isLoading && error == null && listRows.length > 0 && (
          <div className='space-y-4'>
            {hasAnyHazards && (
              <p className='text-sm text-muted-foreground'>
                {t('hazardsPage.generateFromStandardsModal.selectHint')}
              </p>
            )}
            <div className='space-y-4'>
              {listRows.map(({ key, sa, groupItems }) => {
                const standardReference = sa.reference
                if (groupItems.length === 0) {
                  const showDataStatusBadge = applicabilityNeedsDataBadge(sa)
                  return (
                    <Card key={key} className='overflow-hidden'>
                      <div className='flex flex-wrap items-center gap-2 border-b bg-muted/40 px-4 py-2'>
                        <span className='min-w-0 flex-1 text-sm font-medium'>
                          {standardReference}
                        </span>
                        {showDataStatusBadge ? (
                          <StandardDataStatusBadge
                            hasData={
                              sa.hasSignificantHazards === true ||
                              sa.hasInformationForUse === true ||
                              sa.hasRiskReductionMeasures === true
                            }
                            hasPdf={sa.hasFile === true}
                            requireLinkedUserStandard
                            userStandardId={sa.userStandardId}
                          />
                        ) : (
                          <span className='shrink-0 text-xs text-muted-foreground'>
                            {t(
                              'hazardsPage.generateFromStandardsModal.hazardsCount',
                              { count: 0 }
                            )}
                          </span>
                        )}
                      </div>
                    </Card>
                  )
                }
                const selectable = selectableIdsForGroup(groupItems)
                const allSelected =
                  selectable.length > 0 &&
                  selectable.every((id) => selectedIds.has(id))
                const someSelected = selectable.some((id) =>
                  selectedIds.has(id)
                )
                const isExpanded = !collapsedStandards.has(standardReference)
                return (
                  <Card key={key} className='overflow-hidden'>
                    <div className='flex items-center gap-2 border-b bg-muted/40 px-4 py-2'>
                      <button
                        type='button'
                        className='shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground'
                        onClick={() => toggleCollapsed(standardReference)}
                        aria-expanded={isExpanded}
                      >
                        <ChevronDown
                          className={cn('size-5 transition-transform', {
                            'rotate-180': isExpanded,
                          })}
                        />
                      </button>
                      <Checkbox
                        id={`standard-${standardReference}`}
                        checked={allSelected}
                        disabled={selectable.length === 0}
                        onCheckedChange={() =>
                          toggleStandard(standardReference)
                        }
                      />
                      <label
                        htmlFor={`standard-${standardReference}`}
                        className='min-w-0 flex-1 cursor-pointer text-sm font-medium'
                      >
                        {standardReference}
                      </label>
                      <span className='shrink-0 text-xs text-muted-foreground'>
                        {someSelected
                          ? t(
                              'hazardsPage.generateFromStandardsModal.selectedCount',
                              {
                                selected: selectable.filter((id) =>
                                  selectedIds.has(id)
                                ).length,
                                total: selectable.length,
                              }
                            )
                          : t(
                              'hazardsPage.generateFromStandardsModal.hazardsCount',
                              { count: groupItems.length }
                            )}
                      </span>
                    </div>
                    <Collapse open={isExpanded} className='pt-2'>
                      <ItemGroup className='px-2 pb-2'>
                        {groupItems.map((item) => {
                          const rowKey = `${standardReference}-${item.index}`
                          const selectableRow = item.id !== ''
                          const alreadyGenerated =
                            selectableRow &&
                            generatedSignificantHazardIds.has(item.id)
                          return (
                            <Item
                              key={rowKey}
                              variant='muted'
                              className='hover:bg-muted/60'
                            >
                              <div className='shrink-0 pt-0.5'>
                                <Checkbox
                                  id={`hazard-${rowKey}`}
                                  checked={selectedIds.has(item.id)}
                                  disabled={!selectableRow}
                                  onCheckedChange={() => toggleId(item.id)}
                                />
                              </div>
                              <ItemContent>
                                <label
                                  htmlFor={`hazard-${rowKey}`}
                                  className={cn(
                                    'cursor-pointer',
                                    !selectableRow && 'cursor-default'
                                  )}
                                >
                                  <ItemTitle className='flex flex-wrap items-start justify-between gap-2'>
                                    <span className='min-w-0'>
                                      {item.number != null &&
                                      item.number !== ''
                                        ? `${item.number}${item.category != null && item.category !== '' ? ` — ${item.category}` : ''}`
                                        : item.description}
                                    </span>
                                    {alreadyGenerated ? (
                                      <Badge
                                        variant='secondary'
                                        className='shrink-0 font-normal'
                                      >
                                        {t(
                                          'hazardsPage.generateFromStandardsModal.alreadyGenerated'
                                        )}
                                      </Badge>
                                    ) : null}
                                  </ItemTitle>
                                  {item.number != null &&
                                  item.number !== '' &&
                                  item.description ? (
                                    <ItemDescription className='line-clamp-2'>
                                      {item.description}
                                    </ItemDescription>
                                  ) : null}
                                  {!selectableRow ? (
                                    <p className='mt-1 text-xs text-muted-foreground'>
                                      {t(
                                        'hazardsPage.generateFromStandardsModal.missingIdHint'
                                      )}
                                    </p>
                                  ) : null}
                                </label>
                              </ItemContent>
                            </Item>
                          )
                        })}
                      </ItemGroup>
                    </Collapse>
                  </Card>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant='outline' onClick={handleClose} disabled={isGenerating}>
          {t('common:actions.cancel')}
        </Button>
        <Button disabled={!canGenerate} onClick={handleGenerate}>
          {t('hazardsPage.generateFromStandardsModal.generateSelected', {
            count: selectedList.length,
          })}
        </Button>
      </DialogFooter>
    </>
  )
}
